import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Plug, KeyRound, AlertCircle, RefreshCw } from "lucide-react";
import { Card, CardBody } from "../components/Card";
import { Button } from "../components/Button";
import { api } from "../lib/api";

interface ConnectorSummary {
  connectorId: string;
  displayName: string;
  description: string;
  method: "GET" | "POST";
  baseUrl: string;
  hasSecret?: boolean;
  updatedAt: string;
}

export default function Connectors() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ConnectorSummary[] | null>(null);

  useEffect(() => {
    api.get<ConnectorSummary[]>("/api/connectors").then(setItems).catch(() => setItems([]));
  }, []);

  if (items === null) {
    return (
      <div className="flex h-60 items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Connectors</h2>
          <p className="mt-1 text-sm text-slate-500 font-medium">
            HTTP endpoints the AI calls before drafting replies — order lookup, account state, and custom API requests.
          </p>
        </div>
        <Button onClick={() => navigate("/connectors/new")} className="self-start sm:self-center">
          + Create Connector
        </Button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardBody>
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 border border-slate-200/60 text-slate-450">
                <Plug className="h-6 w-6" />
              </div>
              <p className="mt-4 text-base font-bold text-slate-900">No connectors yet</p>
              <p className="mt-1.5 max-w-sm text-sm text-slate-500 font-medium leading-normal">
                Define HTTPS endpoints (like your internal order lookup or user management systems) that Triage can query to fetch real-time context.
              </p>
              <div className="mt-5">
                <Button size="sm" onClick={() => navigate("/connectors/new")}>
                  Create connector
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {items.map((c) => {
              const host = (() => {
                try {
                  return new URL(c.baseUrl).host;
                } catch {
                  return c.baseUrl;
                }
              })();
              return (
                <li key={c.connectorId}>
                  <Link
                    to={`/connectors/${c.connectorId}`}
                    className="group flex items-start gap-4 p-6 hover:bg-slate-50/70 transition-colors"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 border border-brand-100/50 text-brand-650 group-hover:bg-brand-100 transition-colors">
                      <Plug className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <h3 className="truncate text-sm font-bold text-slate-900 group-hover:text-brand-600 transition-colors">
                          {c.displayName || "(unnamed)"}
                        </h3>
                        <MethodBadge method={c.method} />
                        {c.hasSecret ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200/40 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700">
                            <KeyRound className="h-3 w-3 shrink-0" /> auth secret set
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-205/50 px-2.5 py-0.5 text-[10px] font-bold text-amber-700">
                            <AlertCircle className="h-3 w-3 shrink-0" /> no secret configured
                          </span>
                        )}
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs font-semibold text-slate-450 leading-relaxed">
                        {c.description || <span className="italic text-slate-350 font-normal">No description provided.</span>}
                      </p>
                      <div className="mt-3 text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-2 py-0.75 rounded-md inline-block max-w-xs truncate">
                        {host}
                      </div>
                    </div>
                    <ChevronRight className="mt-1.5 h-5 w-5 text-slate-300 group-hover:text-slate-650 transition-colors" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

function MethodBadge({ method }: { method: "GET" | "POST" }) {
  const cls = method === "GET" ? "bg-blue-50 text-blue-700 border-blue-200/50" : "bg-purple-50 text-purple-700 border-purple-200/50";
  return <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[9px] font-bold ${cls}`}>{method}</span>;
}
